
import React from 'react';
import { Music, BookOpen, Clock, UserRound, Award, HeartHandshake } from 'lucide-react';

interface BenefitProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const Benefit = ({ icon, title, description }: BenefitProps) => {
  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 transition-transform hover:translate-y-[-5px]">
      <div className="w-12 h-12 flex items-center justify-center bg-piano-gold/10 text-piano-gold rounded-full mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-serif font-semibold mb-3">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

const BeginnerBenefits = () => {
  const benefits = [
    {
      icon: <UserRound className="w-6 h-6" />,
      title: "Skræddersyet undervisning",
      description: "Undervisningen tilpasses præcis til dit niveau, tempo og musiksmag, så du får den bedste start."
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Solidt fundament",
      description: "Lær de grundlæggende teknikker og musikalske principper, der giver dig et solidt fundament at bygge videre på."
    },
    {
      icon: <Music className="w-6 h-6" />,
      title: "Spil musik du elsker",
      description: "Du kommer hurtigt til at spille enkle versioner af sange, du kender og holder af."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Fleksible tider",
      description: "Undervisning der passer ind i din kalender, både i dagtimerne, aftentimer og i weekenden."
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Erfaren underviser",
      description: "Drag fordel af undervisning med en konservatorieuddannet pianist med mange års undervisningserfaring."
    },
    {
      icon: <HeartHandshake className="w-6 h-6" />,
      title: "Tålmodig vejledning",
      description: "Nyd godt af en rolig og tålmodig tilgang, hvor der er plads til at gentage og stille spørgsmål."
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-serif font-bold mb-4">
            Fordele ved klaverundervisning for begyndere
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Min undervisning for begyndere fokuserer på at give dig en positiv start på din klaverrejse, hvor læring og spilleglæde går hånd i hånd.
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <Benefit 
              key={index}
              icon={benefit.icon}
              title={benefit.title}
              description={benefit.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BeginnerBenefits;
